import BetOptions from 'slotworks/components/betbar/vo/bet-options';
import Components from 'slotworks/components/components';
import ButtonElement, { ButtonEvent } from 'slotworks/graphics/elements/button-element';
import Layers from 'slotworks/graphics/layers/layers';
import currency from 'slotworks/i18n/currency';
import betModel from 'slotworks/model/bets/bet-model';
import Contract from 'slotworks/utils/contract';
import { sequence } from 'slotworks/utils/race-utils';
import slotDefinition from 'slotworks/model/slot-definition';
import revolverModel from 'slotworks/integration/revolver/model/revolver-model';
import { log } from 'util';
import { AxiosRequestConfig } from 'axios';
import commsManager from 'slotworks/server/comms-manager';
import gameState from 'slotworks/model/game-state';
import BuyoutResult from 'integration/buyout-result';
import stateMachine from 'slotworks/state-machine/state-machine';
import deviceInfo from 'slotworks/utils/device-info';
import doSettingsModel from 'model/do-settings-model';

export default class BonusBuyPrompt {
    public static MULTIPLIER_OS: number = 46;
    public static MULTIPLIER_M3: number = 40;

    public static MOBILE_SCALE: number = 1.3;

    private closeButton: ButtonElement;
    private buyOSButton: ButtonElement;
    private plusOSButton: ButtonElement;
    private minusOSButton: ButtonElement;
    private totalBetOSText: PIXI.Text;
    private totalOSValue: PIXI.extras.BitmapText;

    private buyMatch3Button: ButtonElement;
    private plusMatch3Button: ButtonElement;
    private minusMatch3Button: ButtonElement;
    private totalBetMatch3Text: PIXI.Text;
    private totalMatch3Value: PIXI.extras.BitmapText;

    private betOptions: BetOptions;
    private inResolve: Function;

    private enabled: boolean = true;

    constructor(betOptions: BetOptions) {
        this.betOptions = betOptions;
    }

    public show(): Contract<boolean> {
        return new Contract(resolve => {
            Layers.CelebrationBackground.setScene('bonusbuy').execute();
            Layers.Prompts.setScene('bonusbuy').then(() => this.init());
            this.inResolve = resolve;
            if(!deviceInfo.isDesktop) deviceInfo.onLayoutChange.add(this.resetBet);
        });
    }

    private resetBet = () => {
        setTimeout(() => this.updateFields(), 1);
    }

    private init() {
        // this.betOptions.getTotalStakeValues();
        doSettingsModel.savedCoinSize = this.betOptions.coinSize;
        doSettingsModel.savedStakePerLine = this.betOptions.stakePerLine;
        
        this.closeButton = Layers.Prompts.getButton('close');
        this.buyOSButton = Layers.Prompts.getButton('osbuy');
        this.plusOSButton = Layers.Prompts.getButton('osnext');
        this.minusOSButton = Layers.Prompts.getButton('osprev');

        this.totalBetOSText = Layers.Prompts.getText('outlaw_bet');
        this.totalOSValue = Layers.Prompts.getBitmapText('outlaw_value');

        this.buyMatch3Button = Layers.Prompts.getButton('match3buy');
        this.plusMatch3Button = Layers.Prompts.getButton('match3next');
        this.minusMatch3Button = Layers.Prompts.getButton('match3prev');

        this.totalBetMatch3Text = Layers.Prompts.getText('match_bet');
        this.totalMatch3Value = Layers.Prompts.getBitmapText('match_value');

        this.closeButton.on(ButtonEvent.POINTER_DOWN, this.close);

        this.buyOSButton.on(ButtonEvent.POINTER_DOWN, this.onBuySpins);
        this.plusOSButton.on(ButtonEvent.POINTER_DOWN, this.incrementBet);
        this.minusOSButton.on(ButtonEvent.POINTER_DOWN, this.decrementBet);

        this.buyMatch3Button.on(ButtonEvent.POINTER_DOWN, this.onBuyBonus);
        this.plusMatch3Button.on(ButtonEvent.POINTER_DOWN, this.incrementBet);
        this.minusMatch3Button.on(ButtonEvent.POINTER_DOWN, this.decrementBet);
        
        this.setEnabled(true);
        this.updateFields();
    }

    private setEnabled(enabled: boolean) {
        this.enabled = enabled;
        this.closeButton.setEnabled(enabled);
        this.buyOSButton.setEnabled(enabled);
        this.plusOSButton.setEnabled(enabled);
        this.minusOSButton.setEnabled(enabled);
        this.buyMatch3Button.setEnabled(enabled);
        this.plusMatch3Button.setEnabled(enabled);
        this.minusMatch3Button.setEnabled(enabled);

        this.updateBuyButtons();
    }

    private close = () => {
        this.destroy();
        this.betOptions.stakePerLine = doSettingsModel.savedStakePerLine;
        this.betOptions.coinSize = doSettingsModel.savedCoinSize;
        // Components.betBar.setCoinSize(this.betOptions.coinSize);
        // Components.betBar.setStake(this.betOptions.stakePerLine);
        sequence([() => Layers.Prompts.defaultScene(), () => Layers.CelebrationBackground.defaultScene()]).then(() => {
            if (this.inResolve) {
                this.inResolve(false);
                this.inResolve = null;
            }
        });
    };

    private incrementBet = () => {
        let index = this.betOptions.totalStakeValues.indexOf(this.betOptions.getTotalStake());
        if (index < this.betOptions.totalStakeValues.length + 1) {
            index++;
            const currentStake = this.betOptions.totalStakeValues[index];
            this.betOptions.setTotalStake(currentStake);
            this.updateFields();
            this.updateBet();
        }
    };

    private decrementBet = () => {
        let index = this.betOptions.totalStakeValues.indexOf(this.betOptions.getTotalStake());
        if (index > 0) {
            index--;
            const currentStake = this.betOptions.totalStakeValues[index];
            this.betOptions.setTotalStake(currentStake);
            this.updateFields();
            this.updateBet();
        }
    };

    private updateBuyButtons() {
        if(this.enabled) {
            if(this.isBalanceAboveAmountOS()) {
                this.buyOSButton.setEnabled(true);
            } else {
                this.buyOSButton.setEnabled(false);
            }
            if(this.isBalanceAboveAmountM3()) {
                this.buyMatch3Button.setEnabled(true);
            } else {
                this.buyMatch3Button.setEnabled(false);
            }
        }
    }

    private isBalanceAboveAmountOS() {
        const currentBet = this.betOptions.getTotalStake();
        const amount = currentBet * BonusBuyPrompt.MULTIPLIER_OS;
        const balance = gameState.getCurrentGame().balance;
        
        return amount <= balance;
    }

    private isBalanceAboveAmountM3() {
        const currentBet = this.betOptions.getTotalStake();
        const amount = currentBet * BonusBuyPrompt.MULTIPLIER_M3;
        const balance = gameState.getCurrentGame().balance;
        
        return amount <= balance;
    }

    private updateBet() {
        betModel.setCoinSize(this.betOptions.coinSize, true);
        betModel.setBetPerLine(this.betOptions.stakePerLine, true);

        Components.betBar.setCoinSize(this.betOptions.coinSize);
        Components.betBar.setStake(this.betOptions.stakePerLine);
    }

    private updateFields() {
        const currentBet = this.betOptions.getTotalStake();
        this.totalBetOSText.text = currency.format(currentBet);
        this.totalOSValue.text = currency.format(currentBet * BonusBuyPrompt.MULTIPLIER_OS, undefined, undefined, true);

        this.totalBetMatch3Text.text = currency.format(currentBet);
        this.totalMatch3Value.text = currency.format(currentBet * BonusBuyPrompt.MULTIPLIER_M3, undefined, undefined, true);

        this.updateBuyButtons();
    }

    private destroy() {
        if(!deviceInfo.isDesktop) deviceInfo.onLayoutChange.remove(this.resetBet)
        this.closeButton.off(ButtonEvent.POINTER_DOWN, this.close);
        this.buyOSButton.off(ButtonEvent.POINTER_DOWN, this.onBuySpins);
        this.plusOSButton.off(ButtonEvent.POINTER_DOWN, this.incrementBet);
        this.minusOSButton.off(ButtonEvent.POINTER_DOWN, this.decrementBet);
        this.buyMatch3Button.off(ButtonEvent.POINTER_DOWN, this.onBuyBonus);
        this.plusMatch3Button.off(ButtonEvent.POINTER_DOWN, this.incrementBet);
        this.minusMatch3Button.off(ButtonEvent.POINTER_DOWN, this.decrementBet);
    }

    private onBuySpins = () => {
        this.buy(BuyoutTypes.BUYOUT_SPINS);
    };

    private onBuyBonus = () => {
        this.buy(BuyoutTypes.BUYOUT_BONUS);
    };

    private buy(type: BuyoutTypes) {
        this.setEnabled(false);
        this.buyRequest(type).then(() => {
            const record = gameState.getCurrentGame().latestRecord();
            if (record) {
                const result = record.getResultsOfType(BuyoutResult) as BuyoutResult[];
                if (result && result.length > 0) {
                    if(type == BuyoutTypes.BUYOUT_BONUS) {
                        doSettingsModel.isCurrentBonusBuyOut = true;
                    }
                    doSettingsModel.totalBetOnBuyoutBonus = this.betOptions.getTotalStake();
                    Layers.Prompts.defaultScene().then(() => {
                        this.destroy();
                        if (this.inResolve) {
                            this.inResolve(true);
                            this.inResolve = null;
                        }
                        stateMachine.skip();
                    });
                }
            }
        });
    }

    private buyRequest(type: BuyoutTypes): Contract<void> {
        return new Contract(resolve => {
            const coinLevel = slotDefinition.coinSizes.indexOf(this.betOptions.coinSize) + 1;
            const action: any = {
                type: type,
                bet: { bet_level: this.betOptions.stakePerLine / revolverModel.creditBaseMultiplier, coin_level: coinLevel, lines: this.betOptions.lines }
            };

            const requestPayLoad: any = {
                cmd: 'RESULTS',
                additionalData: {},
                session: revolverModel.session,
                action
            };

            try {
                if (revolverModel.getGameCode()) {
                    requestPayLoad.additionalData.gameCode = revolverModel.getGameCode();
                }
                if (revolverModel.getOperator()) {
                    requestPayLoad.additionalData.getOperator = revolverModel.getOperator();
                }
                if (revolverModel.getOperatorId()) {
                    requestPayLoad.additionalData.getOperatorId = revolverModel.getOperatorId();
                }
                if (revolverModel.getBranding()) {
                    requestPayLoad.additionalData.getBranding = revolverModel.getBranding();
                }
                if (window && window.location) {
                    if (window.location.hasOwnProperty('pathname')) {
                        requestPayLoad.additionalData.windowPath = window.location.pathname;
                    }
                }
            } catch (e) {
                log(e);
            }

            const requestConfig = {
                method: 'post',
                url: revolverModel.getBaseURL(),
                withCredentials: true,
                data: JSON.stringify(requestPayLoad)
            } as AxiosRequestConfig;

            if (revolverModel.jwt_token) {
                requestConfig.headers = {
                    'X-RGS-SESSION-TOKEN': revolverModel.jwt_token
                };
            }

            commsManager.requestRaw(requestConfig).then(resolve);
        });
    }
}

export enum BuyoutTypes {
    BUYOUT_SPINS = 'BUYOUT_SPINS',
    BUYOUT_BONUS = 'BUYOUT_BONUS'
}



// WEBPACK FOOTER //
// ./src/components/bonus-buy/bonus-buy-prompt.ts